// The ECharts option for a line or bar widget. Pure, so it is tested without drawing.
import type { Band } from "../expected";
import { formatValue } from "../format";
import type { ChartSeries } from "./series";

/** One drawn series: a bound datapoint's history with its name and format. */
export interface ChartLine {
  name: string;
  unit: string | null;
  decimals: number;
  /** The point's normal band (DDB-03), drawn shaded across the chart. */
  expected?: Band;
  series: ChartSeries;
}

export interface ChartLook {
  kind: "line" | "bar";
  locale: string;
  /** Drag and wheel zoom, which asks the page for the zoomed range. */
  zoom: boolean;
  /** config.stacked: the series are stacked on each other. */
  stacked: boolean;
  /**
   * A zoomed range (epoch ms): the axis spans it and as much again on each side, so the chart can zoom out and pan
   * past the data it has, which covers only the range.
   */
  window?: [number, number];
}

/** The axis around a zoomed range: the range and its own length on each side. */
export function zoomExtent([from, to]: [number, number]): [number, number] {
  const span = to - from;
  return [from - span, to + span];
}

// The brand colour (styles.css --color-brand) first, then colours that stay apart on white for up to eight bindings.
export const COLOURS = ["#1b322a", "#2563eb", "#d97706", "#7c3aed", "#0d9488", "#db2777", "#4b5563", "#65a30d"];
const BAND = "rgba(27, 50, 42, 0.14)";
// The normal band: a light green behind the lines, apart from the brand-coloured min to max band.
const EXPECTED = "rgba(22, 163, 74, 0.10)";
// Late data is shaded amber; estimated times get a dashed outline (WEB-F-DEV-04).
const LATE = "rgba(217, 119, 6, 0.12)";
const ESTIMATED = "#6b7280";

/** The tooltip is HTML, and point names and units come from the API. */
const escape = (text: string) =>
  text.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] ?? c);

/**
 * The normal band to shade on a line chart: when every line that has one has the same, and the chart is not
 * stacked (a stacked total has no normal band).
 */
export function expectedBand(lines: ChartLine[], look: ChartLook): Band | undefined {
  if (look.kind !== "line" || look.stacked || chartUnits(lines).length > 1) return undefined;
  const bands = lines.flatMap((l) => (l.expected ? [l.expected] : []));
  const first = bands[0];
  return first && bands.every((b) => b[0] === first[0] && b[1] === first[1]) ? first : undefined;
}

/** The kinds of value on a chart, in the order of their first line: one value axis each, at most two (DDB-37). */
export function chartUnits(lines: Pick<ChartLine, "unit">[]): (string | null)[] {
  return [...new Set(lines.map((l) => l.unit))];
}

/**
 * The lines a chart draws: those of its first two units. A line of a third unit (an older document, or a picker
 * choice before DDB-37) is left out rather than drawn against another unit's scale; the widget says so.
 */
export function drawable<T extends Pick<ChartLine, "unit">>(lines: T[]): T[] {
  const units = chartUnits(lines).slice(0, 2);
  return lines.filter((l) => units.includes(l.unit));
}

export function chartOption(lines: ChartLine[], look: ChartLook) {
  const time = new Intl.DateTimeFormat(look.locale, { dateStyle: "medium", timeStyle: "short" });
  const units = chartUnits(lines).slice(0, 2);
  const axisOf = (l: ChartLine) => Math.max(0, units.indexOf(l.unit));
  const axisNumber = (axis: number) => {
    const decimals = Math.max(0, ...lines.filter((l) => axisOf(l) === axis).map((l) => l.decimals));
    return (v: number) => formatValue(v, null, decimals, look.locale);
  };
  const series: object[] = [];
  const first = lines[0];
  // A band from min to max, for a single unstacked avg line: an invisible min line with the difference stacked on
  // it as an area.
  const band = look.kind === "line" && lines.length === 1 && !look.stacked ? first?.series.band : undefined;
  if (first && band) {
    const at = (i: number) => first.series.times[i] ?? 0;
    series.push(
      {
        type: "line",
        data: band.min.map((v, i) => [at(i), v]),
        stack: "band",
        symbol: "none",
        lineStyle: { opacity: 0 },
        silent: true,
      },
      {
        type: "line",
        data: band.max.map((v, i) => [at(i), v === null || band.min[i] == null ? null : v - (band.min[i] ?? 0)]),
        stack: "band",
        symbol: "none",
        lineStyle: { opacity: 0 },
        areaStyle: { color: BAND },
        silent: true,
      },
    );
  }
  const normal = expectedBand(lines, look);
  lines.forEach((l, i) => {
    const s = l.series;
    const data = s.values.map((v, j) => [s.times[j] ?? 0, v]);
    const marks = s.quality.map((q) => [
      {
        xAxis: q.from,
        itemStyle:
          q.flag === "late"
            ? { color: LATE }
            : { color: "transparent", borderColor: ESTIMATED, borderType: "dashed", borderWidth: 1 },
      },
      { xAxis: q.to },
    ]);
    const common = {
      id: `line-${i}`,
      name: l.name,
      data,
      color: COLOURS[i % COLOURS.length],
      ...(units.length > 1 ? { yAxisIndex: axisOf(l) } : {}),
      ...(look.stacked ? { stack: `total-${axisOf(l)}` } : {}),
      ...(marks.length || (normal && i === 0)
        ? {
            markArea: {
              silent: true,
              data: [
                ...(normal && i === 0
                  ? [[{ yAxis: normal[0], itemStyle: { color: EXPECTED } }, { yAxis: normal[1] }]]
                  : []),
                ...marks,
              ],
            },
          }
        : {}),
    };
    series.push(
      look.kind === "line"
        ? { type: "line", symbol: "none", connectNulls: false, ...common }
        : { type: "bar", barMaxWidth: 32, ...common },
    );
  });
  return {
    animation: false,
    grid: { left: 8, right: 8, top: units.length > 1 ? 24 : 8, bottom: 8, containLabel: true },
    xAxis: {
      type: "time",
      ...(look.window ? { min: zoomExtent(look.window)[0], max: zoomExtent(look.window)[1] } : {}),
      axisLabel: { hideOverlap: true, color: "#374151" },
    },
    // One value axis per unit: the first on the left, a second on the right with its unit as the name (DDB-37).
    yAxis: units.map((u, axis) => ({
      type: "value",
      scale: look.kind === "line" && !look.stacked,
      axisLabel: { formatter: axisNumber(axis), color: "#374151" },
      position: axis === 0 ? "left" : "right",
      splitLine: axis === 0 ? { lineStyle: { color: "#e5e7eb" } } : { show: false },
      ...(units.length > 1 && u ? { name: u, nameTextStyle: { color: "#374151" } } : {}),
    })),
    tooltip: {
      trigger: "axis",
      formatter: (items: { seriesId?: string; value: [number, number | null] }[]) => {
        const rows = items.flatMap((item) => {
          const i = Number(item.seriesId?.replace(/^line-/, "") ?? NaN);
          const l = item.seriesId?.startsWith("line-") ? lines[i] : undefined;
          if (!l) return [];
          const v = item.value[1];
          const shown = v === null ? "—" : formatValue(v, l.unit, l.decimals, look.locale);
          return [escape(lines.length > 1 ? `${l.name}: ${shown}` : shown)];
        });
        const t = items[0]?.value[0];
        return rows.length && t !== undefined ? [time.format(t), ...rows].join("<br/>") : "";
      },
    },
    // Zoom by dragging, pinching or the wheel; useWheelCapture lets the wheel through only once the pointer has rested
    // on the chart, or with Ctrl, so the page scrolls past charts. No zoom goes below 10 minutes,
    // where a chart with a reading a minute has too few points to draw.
    dataZoom: look.zoom
      ? [
          {
            type: "inside",
            filterMode: "none",
            zoomOnMouseWheel: true,
            moveOnMouseWheel: false,
            preventDefaultMouseMove: false,
            minValueSpan: MIN_ZOOM_MS,
            ...(look.window ? { startValue: look.window[0], endValue: look.window[1] } : {}),
          },
        ]
      : [],
    series,
  };
}

/** The narrowest zoom, in milliseconds. */
export const MIN_ZOOM_MS = 10 * 60_000;
